import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../shared/context/auth-context";
import Card from "../../shared/components/UIElements/Card";
import { openOrSave } from "../../shared/util/fileDownload";

import "./Reports.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });
const DAY   = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const TIME  = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });

function formatSize(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

// "Today, 02:22", "Yesterday, 02:20" or "Mon, 6 Oct 2026, 02:22"
function formatWhen(iso, now = new Date()) {
  const date = new Date(iso);
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 864e5);
  const day = days === 0 ? "Today" : days === 1 ? "Yesterday" : DAY.format(date);
  return `${day}, ${TIME.format(date)}`;
}

// Reports arrive newest first; group them by calendar month, keeping that order
function groupByMonth(reports) {
  const months = [];
  for (const report of reports) {
    const date = new Date(report.createdAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (months.length === 0 || months[months.length - 1].key !== key) {
      months.push({ key, label: MONTH.format(date), items: [] });
    }
    months[months.length - 1].items.push(report);
  }
  return months;
}

// ─── States ───────────────────────────────────────────────────────────────────

function ReportsSkeleton() {
  return (
    <div aria-busy="true">
      <Card className="reports-container rp-latest">
        <div className="rp-latest__body">
          <span className="rp-skeleton rp-skeleton--short" />
          <span className="rp-skeleton rp-skeleton--title" />
          <span className="rp-skeleton rp-skeleton--meta" />
        </div>
      </Card>
      <Card className="reports-container">
        {[0, 1, 2].map((i) => <span key={i} className="rp-skeleton rp-skeleton--row" />)}
      </Card>
    </div>
  );
}

function Message({ title, children, action }) {
  return (
    <Card className="reports-container rp-message">
      <p className="rp-message__title">{title}</p>
      <p className="rp-note">{children}</p>
      {action}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const Reports = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports]         = useState(null); // null while loading
  const [error, setError]             = useState(null); // { status, message }
  const [reloadKey, setReloadKey]     = useState(0);
  const [busy, setBusy]               = useState(null); // { id, action }
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setError(null);
      setReports(null);
      try {
        const res = await fetch("/api/reports/", {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError({ status: res.status, message: data.message });
          return;
        }
        setReports(data.reports);
      } catch (err) {
        if (err.name !== "AbortError") setError({ status: 0 });
      }
    };
    load();
    return () => controller.abort();
  }, [auth.token, reloadKey]);

  const run = async (report, action) => {
    setBusy({ id: report.id, action });
    setActionError(null);
    const view = action === "view";
    try {
      await openOrSave({
        path: `/api/reports/${encodeURIComponent(report.id)}${view ? "?inline=1" : ""}`,
        token: auth.token, view, filename: report.id, errorMessage: "The report couldn't be loaded.",
      });
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const isBusy = (report, action) => busy?.id === report.id && (!action || busy.action === action);

  let content;
  if (error?.status === 403) {
    content = (
      <Message
        title="Reports are part of Signal Access"
        action={<button className="rp-btn" onClick={() => navigate("/usersettings?tab=billing")}>View plan</button>}
      >
        Subscribe to get the nightly report: fund performance, open positions and every trade.
      </Message>
    );
  } else if (error) {
    content = (
      <Message
        title="Couldn't load the reports"
        action={<button className="rp-btn rp-btn--outline" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>}
      >
        {error.message || "Check your connection and try again."}
      </Message>
    );
  } else if (!reports) {
    content = <ReportsSkeleton />;
  } else if (reports.length === 0) {
    content = (
      <Message title="No reports yet">The first report appears after the next nightly run.</Message>
    );
  } else {
    const [latest] = reports;
    content = (
      <>
        <Card className="reports-container rp-latest">
          <div className="rp-latest__body">
            <span className="rp-eyebrow">Latest report</span>
            <h2 className="rp-latest__title">{latest.fund}</h2>
            <p className="rp-meta">
              Generated <time dateTime={latest.createdAt}>{formatWhen(latest.createdAt)}</time> · {formatSize(latest.size)}
            </p>
          </div>
          <div className="rp-actions">
            <button className="rp-btn rp-btn--outline" onClick={() => run(latest, "view")} disabled={isBusy(latest)}>
              {isBusy(latest, "view") ? "Opening…" : "View"}
            </button>
            <button className="rp-btn" onClick={() => run(latest, "download")} disabled={isBusy(latest)}>
              {isBusy(latest, "download") ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </Card>

        {actionError && <p className="rp-error" role="alert">{actionError}</p>}

        <Card className="reports-container">
          <h2 className="rp-section-title">
            Archive <span>{reports.length} {reports.length === 1 ? "report" : "reports"}</span>
          </h2>
          <p className="rp-note">Every report from the last 30 days, then the last report of each month.</p>
          {groupByMonth(reports).map((month, index) => (
            <details key={month.key} className="rp-month" open={index === 0}>
              <summary>
                {month.label}
                <span className="rp-count">{month.items.length} {month.items.length === 1 ? "report" : "reports"}</span>
              </summary>
              <ul className="rp-list">
                {month.items.map((report) => (
                  <li key={report.id} className="rp-row">
                    <time dateTime={report.createdAt} className="rp-row__when">{formatWhen(report.createdAt)}</time>
                    <span className="rp-row__fund">{report.fund}</span>
                    <span className="rp-row__size">{formatSize(report.size)}</span>
                    <span className="rp-row__actions">
                      <button className="rp-link" onClick={() => run(report, "view")} disabled={isBusy(report)}
                              aria-label={`View report of ${formatWhen(report.createdAt)}`}>
                        {isBusy(report, "view") ? "Opening…" : "View"}
                      </button>
                      <button className="rp-link" onClick={() => run(report, "download")} disabled={isBusy(report)}
                              aria-label={`Download report of ${formatWhen(report.createdAt)}`}>
                        {isBusy(report, "download") ? "Preparing…" : "Download"}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </Card>
      </>
    );
  }

  return (
    <div className="rp-page">
      <Card className="reports-container" style={{ textAlign: "center", marginBottom: "20px" }}>
        <h1 style={{ color: "var(--color-dim)", fontSize: 13, fontWeight: 600, margin: "0 0 16px" }}>
          Reports
        </h1>
        <h2 style={{ color: "var(--color-dim)", fontSize: 13, fontWeight: 400, margin: 0 }}>
          A new report every night, after the fund is recalculated
        </h2>
      </Card>
      {content}
    </div>
  );
};

export default Reports;
